import { ChangeDetectionStrategy, Component } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import { DateService } from '@core/date/date.service';
import { days, months, years } from '@core/utils/date.utils';
import { ContactUsReceiptCMSDataContract } from '@models/contact-us-receipt.models';
import { MessageTitleAndContent } from '@shared/types/cms-frequently-used-shapes';
import { emailValidator } from '@shared/validators';
import { RequestAReceiptFacade } from '@store/request-a-receipt/request-a-receipt.facade';
import { RequestReceiptPayload } from '@store/request-a-receipt/types/reqest-a-receipt.state.type';
import { addMonths, getDaysInMonth, getYear, subMonths } from 'date-fns';

import { unwrapBodyTextHierarchy } from '../shared/types/cms-payload-with-bodytext.transform';
import { unwrapComponentMainContent } from './types/contact-us-receipt.transform';
import { ContactUsMainContentUnwrapped } from './types/contact-us-receipt.type';

@Component({
  selector: 'jb-contact-us-receipt',
  templateUrl: './contact-us-receipt.component.html',
  styleUrls: ['./contact-us-receipt.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ContactUsReceiptComponent {
  get firstNameField() {
    return this.submitReceiptForm.get('firstNameField');
  }

  get flightDateField() {
    return this.submitReceiptForm.get('flightDateField');
  }

  get lastNameField() {
    return this.submitReceiptForm.get('lastNameField');
  }

  get confirmationCodeField() {
    return this.submitReceiptForm.get('confirmationCodeField');
  }

  get emailField() {
    return this.submitReceiptForm.get('emailField');
  }

  mainContent: ContactUsMainContentUnwrapped;
  success: MessageTitleAndContent;
  failure: MessageTitleAndContent;
  months = months;
  days = days;
  years;
  maxYear: Number;
  minYear: Number;
  submitReceiptForm: UntypedFormGroup;

  isSubmitted$ = this.requestReceiptFacade.requestReceiptFormIsSubmitted;
  submissionSucceeded$ =
    this.requestReceiptFacade.requestReceiptSubmissionSucceeded;
  submissionFailed$ = this.requestReceiptFacade.requestReceiptSubmissionFailed;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private requestReceiptFacade: RequestAReceiptFacade,
    private dateService: DateService,
  ) {
    this.maxYear = getYear(addMonths(dateService.getNewDate(), 13));
    this.minYear = getYear(subMonths(dateService.getNewDate(), 13));
    this.years = years(this.minYear, this.maxYear);
    this.createSubmitReceiptForm();
  }

  composePayload(formData) {
    const flightDate = `${formData.value['flightDateField']['month']}-${formData.value['flightDateField']['day']}-${formData.value['flightDateField']['year']}`;
    const requestReceiptPayload: RequestReceiptPayload = {
      RecordLocator: formData.get('confirmationCodeField').value,
      EmailAddress: formData.get('emailField').value,
      CustomerFirstName: formData.get('firstNameField').value,
      CustomerLastName: formData.get('lastNameField').value,
      DepartureDate: flightDate,
    };
    return requestReceiptPayload;
  }

  dateValidation(control: AbstractControl): { [err: string]: any } {
    const year = control.value['flightDateField']['year'];
    const month = control.value['flightDateField']['month'];
    const day = control.value['flightDateField']['day'];

    if (year === '' || month === '' || day === '') {
      // these dates are not valid but validator will be still requiring to fill on Validatorr.required
      return null;
    }

    const selectedDate = new Date(
      parseFloat(year),
      parseFloat(month) - 1,
      parseFloat(day),
    );
    const thirteenMonthsAgo = subMonths(
      this.dateService.getNewDate().getTime(),
      13,
    );
    if (selectedDate.getTime() < thirteenMonthsAgo.getTime()) {
      return {
        displayUnderMinMessage: true,
        dateInvalid: true,
      };
    }
    if (
      parseFloat(month) === 2 &&
      parseFloat(day) > getDaysInMonth(new Date(parseFloat(year), 1, 1))
    ) {
      return { dateInvalid: true };
    }
    return null;
  }

  submitReceipt() {
    if (this.submitReceiptForm.valid) {
      const submitRequestReceiptPayload = this.composePayload(
        this.submitReceiptForm,
      );
      this.requestReceiptFacade.requestAReceiptSubmission(
        submitRequestReceiptPayload,
      );
    } else {
      return;
    }
  }

  private createSubmitReceiptForm() {
    this.submitReceiptForm = this.formBuilder.group(
      {
        firstNameField: [undefined, [Validators.required]],
        lastNameField: [undefined, [Validators.required]],
        flightDateField: this.formBuilder.group({
          month: ['', Validators.required],
          day: ['', Validators.required],
          year: ['', Validators.required],
        }),
        confirmationCodeField: [
          undefined,
          [
            Validators.required,
            Validators.minLength(6),
            Validators.maxLength(6),
            Validators.pattern(/^[A-Z]+$/i),
          ],
        ],
        emailField: [undefined, [Validators.required, emailValidator]],
      },
      { validator: this.dateValidation.bind(this) },
    );
  }
}

export const contactUsReceiptContainerResolver = (
  registry,
  data: ContactUsReceiptCMSDataContract,
) => {
  const mainContent = unwrapComponentMainContent(data);
  const success = unwrapBodyTextHierarchy(data.successMessage);
  const failure = unwrapBodyTextHierarchy(data.failureMessage);
  return {
    mainContent,
    success,
    failure,
  };
};
